/** The words of the category's "Activities in this category" panel (plan §4): how many
 *  activities the category holds, what each row says about its activity, where the full
 *  list lives, and what an empty category means. Pure. The activity's own words — its
 *  title and its type's one word — come from the activity record's names, so a row reads
 *  as the activity's header does. */
import type { ActivityRow } from '@/api/activities';
import type { ActivityCategoryRow } from '@/api/activityCategories';
import { COUNT_UNKNOWN } from '@/api/countStatus';
import type { PaginatedResponse } from '@/api/users';
import { activityTitle, activityTypeLabel } from '@/features/activities/record/parts/activityNames';
import { activitiesHref, consoleRouteFor } from '@/utils/gridLinks';

/** The category's first activities: the first page of the activities list filtered by it
 *  (`GET /activities/?category=<id>`). `count` is every activity in the category, not only
 *  this page's; `COUNT_UNKNOWN` (-1) when the list ran without a count. */
export type CategoryActivities = Pick<PaginatedResponse<ActivityRow>, 'count' | 'results'>;

/** The first rows the panel shows; the rest are in the activities grid. */
export const ACTIVITIES_FIRST = 10;

export const ACTIVITIES_EMPTY = 'No activities in this category — safe to delete';

/** "1 activity", "1,240 activities". */
export function activityCountLabel(n: number): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? 'activity' : 'activities'}`;
}

/** The activities grid filtered by the category, on the category's operator. */
export function categoryActivitiesHref(c: Pick<ActivityCategoryRow, 'id' | 'tenant'>): string {
  return activitiesHref([], c.tenant.id, { category: c.id });
}

/** How many activities the category holds: the list's own count, else the detail's
 *  `activity_count` (while the list loads, or when it ran without a count) — except that a
 *  loaded, empty list means none, whatever the detail said when it loaded. */
export function categoryActivityCount(
  list: CategoryActivities | undefined,
  detail: Pick<ActivityCategoryRow, 'activity_count'>,
): number {
  if (!list) return detail.activity_count;
  if (list.count !== COUNT_UNKNOWN) return list.count;
  return list.results.length ? detail.activity_count : 0;
}

/** The panel's rows while they load, from how many activities the category holds: as many
 *  as the loaded panel shows (at most `ACTIVITIES_FIRST`), whether "N more" follows them, and
 *  none — the empty line — for an empty category. */
export function activitiesSkeleton(count: number): { rows: number; more: boolean } {
  return { rows: Math.max(0, Math.min(count, ACTIVITIES_FIRST)), more: count > ACTIVITIES_FIRST };
}

/** The row's name: what the app shows (`display_name || name`, never empty). */
export function activityRowName(row: ActivityRow): string {
  return activityTitle(row);
}

/** The row's facts after its name: the type's one word, then the country when known. */
export function activityRowFacts(row: ActivityRow): string[] {
  return [activityTypeLabel(row), row.country_name?.trim() || null].filter((f): f is string => !!f);
}

/** The row's console page. */
export function activityRowHref(row: Pick<ActivityRow, 'id'>): string | null {
  return consoleRouteFor('activity', row.id);
}
